import React, { useState, useEffect } from 'react';
import { getImagesByFolder } from '../../lib/imageStorage';
import { booksApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { FileDown, FileText, Download, Loader2, Image, BookOpen } from 'lucide-react';

const ExportButton = ({ book, chapters, characters = [] }) => {
    const [open, setOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportFormat, setExportFormat] = useState('pdf');
    const [includeImageMarkers, setIncludeImageMarkers] = useState(true);
    const [imageData, setImageData] = useState({});

    // Load image data when dialog opens
    useEffect(() => {
        if (open && book?.id && characters.length > 0) {
            const folders = getImagesByFolder(book.id, characters);
            setImageData(folders);
        }
    }, [open, book?.id, characters]);

    // Get images for a specific chapter
    const getChapterImages = (chapterTitle) => {
        const chapterImages = [];
        Object.values(imageData).forEach(folder => {
            folder.images.forEach(img => {
                if (img.bookLocation?.chapter === chapterTitle) {
                    chapterImages.push({
                        ...img,
                        characterName: folder.characterName
                    });
                }
            });
        });
        return chapterImages;
    };

    // Generate image marker HTML
    const generateImageMarker = (img) => {
        const position = img.position || 'Full width';
        return `
            <div class="image-marker">
                <div class="image-marker-header">
                    <span class="image-icon">🖼</span>
                    <span class="image-title">${img.title || img.characterName}</span>
                </div>
                <div class="image-position">Position: ${position}</div>
                ${img.bookLocation?.page ? `<div class="image-page">Page: ${img.bookLocation.page}</div>` : ''}
                <img src="${img.url}" alt="${img.title}" class="image-preview" />
            </div>
        `;
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            // ePub export via backend
            if (exportFormat === 'epub') {
                const response = await booksApi.exportEpub(book.id);
                const blob = new Blob([response.data], { type: 'application/epub+zip' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${book.title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}.epub`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success('ePub exported successfully!');
                setOpen(false);
                return;
            }

            // DOCX export via backend
            if (exportFormat === 'docx') {
                const response = await booksApi.exportDocx(book.id);
                const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${book.title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}.docx`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success('DOCX exported successfully!');
                setOpen(false);
                return;
            }

            // Gather all chapter content with image markers
            const bookContent = chapters.map(ch => {
                const chapterImages = includeImageMarkers ? getChapterImages(ch.title) : [];
                return {
                    title: ch.title,
                    content: ch.content || '',
                    type: ch.section_type || 'chapter',
                    images: chapterImages
                };
            });

            if (exportFormat === 'pdf') {
                // Generate PDF using browser print with image markers
                const printWindow = window.open('', '_blank');
                printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>${book.title}</title>
                        <style>
                            @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,600;1,400&display=swap');
                            body { 
                                font-family: 'EB Garamond', Georgia, serif; 
                                max-width: 6in; 
                                margin: 0 auto; 
                                padding: 1in;
                                font-size: 12pt;
                                line-height: 1.6;
                                color: #1a1a1a;
                            }
                            h1 { 
                                font-size: 24pt; 
                                text-align: center; 
                                margin-bottom: 2em;
                                page-break-after: always;
                            }
                            h2 { 
                                font-size: 18pt; 
                                margin-top: 2em;
                                page-break-before: always;
                            }
                            p { margin: 1em 0; text-indent: 1.5em; }
                            p:first-of-type { text-indent: 0; }
                            .chapter { page-break-before: always; }
                            
                            /* Image Marker Styles */
                            .image-marker {
                                border: 2px dashed #d4a373;
                                border-radius: 8px;
                                padding: 16px;
                                margin: 24px 0;
                                background: #fefae0;
                                page-break-inside: avoid;
                            }
                            .image-marker-header {
                                display: flex;
                                align-items: center;
                                gap: 8px;
                                font-weight: 600;
                                color: #bc6c25;
                                margin-bottom: 8px;
                            }
                            .image-icon {
                                font-size: 20px;
                            }
                            .image-title {
                                font-size: 14pt;
                            }
                            .image-position, .image-page {
                                font-size: 10pt;
                                color: #606c38;
                                margin: 4px 0;
                            }
                            .image-preview {
                                max-width: 200px;
                                max-height: 200px;
                                margin-top: 12px;
                                border-radius: 4px;
                                border: 1px solid #ddd;
                            }
                            .images-section {
                                margin-top: 24px;
                                padding-top: 16px;
                                border-top: 1px solid #ddd;
                            }
                            .images-section-title {
                                font-size: 11pt;
                                font-weight: 600;
                                color: #666;
                                margin-bottom: 12px;
                            }
                            
                            @media print {
                                body { padding: 0; }
                                .image-marker {
                                    background: #fff9e6;
                                    border-color: #cc8800;
                                }
                            }
                        </style>
                    </head>
                    <body>
                        <h1>${book.title}</h1>
                        ${bookContent.map(ch => `
                            <div class="chapter">
                                <h2>${ch.title}</h2>
                                <div>${ch.content.replace(/\n/g, '</p><p>')}</div>
                                ${ch.images.length > 0 ? `
                                    <div class="images-section">
                                        <div class="images-section-title">📍 Image Placement Markers for ${ch.title}</div>
                                        ${ch.images.map(img => generateImageMarker(img)).join('')}
                                    </div>
                                ` : ''}
                            </div>
                        `).join('')}
                    </body>
                    </html>
                `);
                printWindow.document.close();
                printWindow.print();
                toast.success('PDF export ready - use Print to PDF');
            } else {
                // Generate simple HTML/text export with image notes
                let content = `# ${book.title}\n\n`;
                bookContent.forEach(ch => {
                    content += `## ${ch.title}\n\n${ch.content}\n\n`;
                    if (ch.images.length > 0 && includeImageMarkers) {
                        content += `### Image Placements for ${ch.title}\n`;
                        ch.images.forEach(img => {
                            content += `- ${img.title || img.characterName}: ${img.position || 'Full width'}`;
                            if (img.bookLocation?.page) content += ` (Page ${img.bookLocation.page})`;
                            content += `\n`;
                        });
                        content += '\n';
                    }
                });
                
                const blob = new Blob([content], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${book.title}.txt`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success('Book exported successfully');
            }
            
            setOpen(false);
        } catch (error) {
            toast.error('Export failed');
        } finally {
            setExporting(false);
        }
    };

    // Count total images with positions
    const totalImages = Object.values(imageData).reduce((sum, folder) => 
        sum + folder.images.filter(img => img.bookLocation?.chapter).length, 0
    );

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" data-testid="export-btn">
                    <FileDown className="h-4 w-4 mr-2" />
                    Export
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-serif">Export Book</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                    <div>
                        <Label>Export Format</Label>
                        <div className="grid grid-cols-4 gap-3 mt-2">
                            <button
                                className={`p-4 rounded-lg border text-left transition-colors ${
                                    exportFormat === 'epub' 
                                        ? 'border-accent bg-accent/10' 
                                        : 'border-border hover:border-accent/50'
                                }`}
                                onClick={() => setExportFormat('epub')}
                                data-testid="export-epub-btn"
                            >
                                <BookOpen className="h-6 w-6 mb-2 text-accent" />
                                <div className="font-medium">ePub</div>
                                <div className="text-xs text-muted-foreground">E-readers</div>
                            </button>
                            <button
                                className={`p-4 rounded-lg border text-left transition-colors ${
                                    exportFormat === 'docx' 
                                        ? 'border-accent bg-accent/10' 
                                        : 'border-border hover:border-accent/50'
                                }`}
                                onClick={() => setExportFormat('docx')}
                                data-testid="export-docx-btn"
                            >
                                <FileText className="h-6 w-6 mb-2 text-accent" />
                                <div className="font-medium">DOCX</div>
                                <div className="text-xs text-muted-foreground">Word</div>
                            </button>
                            <button
                                className={`p-4 rounded-lg border text-left transition-colors ${
                                    exportFormat === 'pdf' 
                                        ? 'border-accent bg-accent/10' 
                                        : 'border-border hover:border-accent/50'
                                }`}
                                onClick={() => setExportFormat('pdf')}
                            >
                                <FileDown className="h-6 w-6 mb-2 text-accent" />
                                <div className="font-medium">PDF</div>
                                <div className="text-xs text-muted-foreground">Print</div>
                            </button>
                            <button
                                className={`p-4 rounded-lg border text-left transition-colors ${
                                    exportFormat === 'txt' 
                                        ? 'border-accent bg-accent/10' 
                                        : 'border-border hover:border-accent/50'
                                }`}
                                onClick={() => setExportFormat('txt')}
                            >
                                <FileText className="h-6 w-6 mb-2 text-muted-foreground" />
                                <div className="font-medium">Text</div>
                                <div className="text-xs text-muted-foreground">Plain</div>
                            </button>
                        </div>
                    </div>

                    {/* Image Position Markers Option - only for PDF/TXT */}
                    {!['epub', 'docx'].includes(exportFormat) && (
                        <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                            <Checkbox 
                                id="imageMarkers"
                                checked={includeImageMarkers}
                                onCheckedChange={setIncludeImageMarkers}
                            />
                            <div className="flex-1">
                                <label htmlFor="imageMarkers" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                                    <Image className="h-4 w-4 text-accent" />
                                    Include Image Position Markers
                                </label>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Shows where illustrations should be placed in the final print layout.
                                    {totalImages > 0 && ` (${totalImages} image${totalImages !== 1 ? 's' : ''} with positions)`}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ePub info */}
                    {exportFormat === 'epub' && (
                        <div className="p-3 bg-accent/10 rounded-lg text-sm border border-accent/20">
                            <div className="font-medium text-accent mb-1">ePub Export</div>
                            <p className="text-muted-foreground text-xs">
                                Creates a professional ePub file compatible with Kindle, Apple Books, Kobo, and other e-readers.
                                Includes table of contents and proper chapter formatting.
                            </p>
                        </div>
                    )}

                    {/* DOCX info */}
                    {exportFormat === 'docx' && (
                        <div className="p-3 bg-accent/10 rounded-lg text-sm border border-accent/20">
                            <div className="font-medium text-accent mb-1">DOCX Export</div>
                            <p className="text-muted-foreground text-xs">
                                Creates a professionally formatted Word document with proper styles, chapter headings,
                                and scene breaks. Perfect for editing or submission to publishers.
                            </p>
                        </div>
                    )}

                    <div className="p-3 bg-muted/50 rounded-lg text-sm">
                        <div className="font-medium mb-1">{book.title}</div>
                        <div className="text-muted-foreground">
                            {chapters.length} chapters - {chapters.reduce((sum, ch) => sum + (ch.word_count || 0), 0).toLocaleString()} words
                        </div>
                    </div>

                    <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={handleExport} disabled={exporting}>
                            {exporting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <Download className="h-4 w-4 mr-2" />
                                    Export {exportFormat.toUpperCase()}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ExportButton;
